import type { ReactNode } from 'react';
import { ChevronRight, File } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReferralStatusChip } from '@/components/patient/ReferralStatusChip';
import { PatientTabFrame } from '@/components/patient/PatientTabFrame';
import {
  REFERRAL_OUTBOX_KINDS,
  useReferralOutbox,
  type OutboxRow,
} from '@/components/patient/referralOutboxData';
import { patientHenvisningDetailPath } from '@/lib/patientRecordPaths';
import { formatCourseDateTime } from '@/lib/courseDateFormat';
import { knownRefuseCode } from '@/lib/referral/referralReceipt';
import { outboundReferralKindLabel, refuseCodeLabel } from '@/lib/referral/outboundReferralLabels';
import { cn } from '@/utils/cn';

export function PatientHenvisningSentList({
  clinicId,
  patientId,
  highlightId,
  switcher,
}: {
  clinicId: string;
  patientId: string;
  highlightId?: string | null;
  switcher: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const query = useReferralOutbox(clinicId, patientId);
  const rows = (query.data?.rows ?? []).filter((row) => REFERRAL_OUTBOX_KINDS.includes(row.kind));
  const highlighted =
    highlightId === 'latest' ? rows[0]?.id : highlightId && rows.some((row) => row.id === highlightId)
      ? highlightId
      : null;

  return (
    <PatientTabFrame
      title={t('patients.tabs.henvisning')}
      action={switcher}
      loading={query.isLoading && !query.data}
      error={query.isError ? t('patients.hospital_referral.outbox_list_failed', 'Udbakken kunne ikke hentes.') : undefined}
      onRetry={() => void query.refetch()}
      empty={
        !query.isLoading && !query.isError && rows.length === 0
          ? {
              icon: <File className="h-7 w-7" />,
              title: t('patients.hospital_referral.sent_empty', 'Ingen sendte henvisninger'),
              description: t(
                'patients.hospital_referral.sent_empty_hint',
                'Elektroniske henvisninger sendt for patienten. Nye henvisninger oprettes med Henvis øverst på siden.',
              ),
            }
          : undefined
      }
    >
      <p className="hv-sub">
        {t(
          'patients.hospital_referral.sent_subtitle',
          'Elektroniske henvisninger sendt for patienten. Nye henvisninger oprettes med Henvis øverst på siden.',
        )}
      </p>
      {query.data?.syncFailed ? (
        <p role="alert" className="mt-s3 text-[14px] text-ds-red-ink">
          {t(
            'patients.hospital_referral.outbox_sync_failed',
            'Kvitteringerne kunne ikke kontrolleres. Status vises, som den er gemt.',
          )}
        </p>
      ) : null}
      <table className="hv-tbl">
        <thead>
          <tr>
            <th>{t('patients.hospital_referral.receiver_section', 'Modtager')}</th>
            <th>{t('patients.hospital_referral.outbox_kind', 'Henvisningstype')}</th>
            <th>{t('patients.hospital_referral.outbox_sent_at', 'Afsendt')}</th>
            <th>{t('patients.hospital_referral.outbox_status', 'Status')}</th>
            <th>{t('patients.hospital_referral.outbox_receipt_at', 'Kvittering modtaget')}</th>
            <th>
              <span className="sr-only">{t('common.open', 'Åbn')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <SentRow
              key={row.id}
              row={row}
              patientId={patientId}
              highlighted={row.id === highlighted}
              receiptAt={formatCourseDateTime(query.data?.receiptReceivedAtById[row.id], i18n.language) || null}
            />
          ))}
        </tbody>
      </table>
      <div className="hv-cards">
        {rows.map((row) => (
          <SentCard
            key={row.id}
            row={row}
            patientId={patientId}
            highlighted={row.id === highlighted}
            receiptAt={formatCourseDateTime(query.data?.receiptReceivedAtById[row.id], i18n.language) || null}
          />
        ))}
      </div>
    </PatientTabFrame>
  );
}

function SentRow({
  row,
  patientId,
  highlighted,
  receiptAt,
}: {
  row: OutboxRow;
  patientId: string;
  highlighted: boolean;
  receiptAt: string | null;
}) {
  const { t, i18n } = useTranslation();
  const refuse = knownRefuseCode(row.refuse_code);
  const waiting = row.status === 'built';
  const to = patientHenvisningDetailPath(patientId, row.id);
  const receiver = row.receiver_organisation_name || row.receiver_ean;
  return (
    <tr className={highlighted ? 'new' : undefined}>
      <td>
        <Link to={to} className="block">
          <div className="nm">{receiver}</div>
          <div className="sub num">EAN {row.receiver_ean}</div>
        </Link>
      </td>
      <td>{outboundReferralKindLabel(row.kind, t)}</td>
      <td className="nw num">{formatCourseDateTime(row.created_at, i18n.language)}</td>
      <td>
        <ReferralStatusChip status={row.status} />
        {refuse ? (
          <div className="sub">
            {t('patients.hospital_referral.outbox_refuse', 'Afvisningskode')}: {refuseCodeLabel(refuse, t)}
          </div>
        ) : null}
      </td>
      <td className={cn('nw num', waiting && 'sub')}>
        {receiptAt ?? t('patients.hospital_referral.outbox_receipt_at_none', 'Ingen kvittering endnu')}
      </td>
      <td>
        <Link
          to={to}
          className="chev"
          aria-label={t('patients.hospital_referral.open_to', 'Åbn henvisning til {{name}}', { name: receiver })}
        >
          <ChevronRight className="h-5 w-5" aria-hidden />
        </Link>
      </td>
    </tr>
  );
}

function SentCard({
  row,
  patientId,
  highlighted,
  receiptAt,
}: {
  row: OutboxRow;
  patientId: string;
  highlighted: boolean;
  receiptAt: string | null;
}) {
  const { t, i18n } = useTranslation();
  const refuse = knownRefuseCode(row.refuse_code);
  const waiting = row.status === 'built';
  const receiver = row.receiver_organisation_name || row.receiver_ean;
  return (
    <Link
      to={patientHenvisningDetailPath(patientId, row.id)}
      className={cn('hv-card', highlighted && 'new')}
      aria-label={t('patients.hospital_referral.open_to', 'Åbn henvisning til {{name}}', { name: receiver })}
    >
      <div className="top">
        <span className="nm">{receiver}</span>
        <span className="chev">
          <ChevronRight className="h-5 w-5" aria-hidden />
        </span>
      </div>
      <div className="hv-m">{outboundReferralKindLabel(row.kind, t)}</div>
      <ReferralStatusChip status={row.status} />
      <div className="hv-m num">
        {t('patients.hospital_referral.outbox_sent_at', 'Afsendt')} {formatCourseDateTime(row.created_at, i18n.language)}
        <br />
        {waiting
          ? t('patients.hospital_referral.outbox_receipt_at_none', 'Ingen kvittering endnu')
          : `${t('patients.hospital_referral.outbox_receipt_at', 'Kvittering modtaget')} ${receiptAt ?? ''}`}
        {refuse ? ` · ${refuseCodeLabel(refuse, t)}` : ''}
      </div>
    </Link>
  );
}
